import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Buttons from "../../components/ui/button/Buttons";
import Select from "react-select";
import { decryptAESID } from "../../utils/Encryption";
import { useNavigate, useParams } from "react-router-dom";
import ModalGroupingTable from "./components/ModalGroupingTable";
import CostAllocation from "./components/CostAllocation";
import PageLoader from "../../components/ui/pageLoader/PageLoader";
import SwalAlert from "../../services/swalService/SwalService";
import { useLazyGetProductDetailsListByProductInformationIdQuery, useCostAlloctionBySGBPProducatInformationIdMutation, useLazyGetCertificateGroupingDetailsByIdQuery, useSendCertificateGroupingRequestForReviewMutation, useValidateCertificateNumberForAssignmentMutation, useSaveFinalCertificateAndCostAllocationMutation } from "../../app/services/CertificateGrouping/certificateGroupingApi";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../app/services/productAndService/productAndServiceApi";
import ToastService from "../../services/toastService/ToastService";
import { useLazyGetPeerReviewEvaluationDetailsByIdQuery } from "../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";

const CertificateGroupingRequestList = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const firstRowOriginalRef = useRef(null);
  const { success, error } = SwalAlert();

  const [productList, setProductList] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);
  const [internalNotes, setInternalNotes] = useState("");
  const [approver, setApprover] = useState();
  const [peerReviewer, setPeerReviewer] = useState();
  const [costAllocationList, setCostAllocationList] = useState([]);
  const [isReview, setIsReview] = useState(false);
  const [isApprove, setIsApprove] = useState(false);
  const [formErrors, setFormErrors] = useState({ costAllocation: {}, peerReviewer: "", approver: "" });
  const [appNumberValues, setAppNumberValues] = useState({});


  const [getProductDetailsListByProductInformationId, { isLoading: productListLoading, isSuccess: productListSuccess, data: productListData }] = useLazyGetProductDetailsListByProductInformationIdQuery();
  const [getCertificateGroupingDetailsById, { isLoading: certificateGroupingDetailsLoading, isSuccess: certificateGroupingDetailsSuccess, data: certificateGroupingDetailsData }] = useLazyGetCertificateGroupingDetailsByIdQuery();
  const [saveCertificateGrouping, { isLoading: saveCertificateGroupingLoading, isSuccess: saveCertificateGroupingSuccess, data: saveCertificateGroupingData }] = useCostAlloctionBySGBPProducatInformationIdMutation();
  const [productsAndServicesDropdown, { data: productsAndServicesDropDownsData, isLoading: productsAndServicesDropDownsLoading }] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [sendCertificateGroupingForReview, { isLoading: sendCertificateGroupingForReviewLoading, isSuccess: sendCertificateGroupingForReviewSuccess, data: sendCertificateGroupingForReviewData }] = useSendCertificateGroupingRequestForReviewMutation();
  const [validateCertificateNumberForAssignment, { isLoading: checkCernumLoading }] = useValidateCertificateNumberForAssignmentMutation();
  const [sendFinalCertificateAndCostAllocation, { data: sendFinalCertificateAndCostAllocationData }] = useSaveFinalCertificateAndCostAllocationMutation();
  const [getPeerReviewEvaluationDetailsById, { isLoading: getPeerReviewEvaluationDetailsByIdLoading, isSuccess: getPeerReviewEvaluationDetailsByIdSuccess, data: getPeerReviewEvaluationDetailsByIdData, },] = useLazyGetPeerReviewEvaluationDetailsByIdQuery();

  // Single source of truth for dropdown options — computed once per data change,
  // reused across every effect/handler instead of re-filtering repeatedly.
  const approverOptionsMemo = useMemo(() => productsAndServicesDropDownsData?.filter((item) => item.dropdownName === "Approver List")
    .map((item) => ({ value: item.id, label: item.name })) ?? []
    , [productsAndServicesDropDownsData]);

  const itemCodeOptionsMemo = useMemo(() => productsAndServicesDropDownsData
    ?.filter((item) => item.dropdownName === "Items Codes")
    .map((item) => ({ value: item.id, label: item.name })) ?? []
    , [productsAndServicesDropDownsData]
  );

  useEffect(() => {
    const container = document.querySelector(".center-container");
    if (container) {
      container.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    const editID = decryptAESID(id, false);
    if (editID > 0) {
      productsAndServicesDropdown();
      getProductDetailsListByProductInformationId(editID);
      getCertificateGroupingDetailsById(editID);
      getPeerReviewEvaluationDetailsById(editID);
    }
  }, [id]);

  useEffect(() => {
    if (!productsAndServicesDropDownsLoading && productsAndServicesDropDownsData) {
      setApproverOptions(approverOptionsMemo);
    }
  }, [productsAndServicesDropDownsLoading, productsAndServicesDropDownsData, approverOptionsMemo]);

  useEffect(() => {
    if (!productListLoading && productListSuccess && productListData) {
      setProductList(productListData);
      const list = Array.isArray(productListData) ? productListData : productListData?.data || [];
      firstRowOriginalRef.current = list.length > 0 ? list[0].applicationNumber : null;
      //  try
      const initialApp = {};
      productListData?.forEach((item) => { initialApp[item.sgbpProductId] = item.applicationNumber ?? ""; });
      setAppNumberValues(initialApp);

    }
  }, [productListLoading, productListSuccess, productListData]);

  useEffect(() => {
    if (productList && productList.length > 0) {
      const list = Array.isArray(productList) ? productList : productList?.data || [];

      setCostAllocationList((prevRows) =>
        buildCostAllocationRows(list, itemCodeOptionsMemo, prevRows)
      );
    }
  }, [productList, itemCodeOptionsMemo]);

  useEffect(() => {
    if (!certificateGroupingDetailsLoading && certificateGroupingDetailsSuccess && certificateGroupingDetailsData && productListData) {

      if (certificateGroupingDetailsData?.allocationCostList?.length > 0) {
        setIsApprove(certificateGroupingDetailsData?.statusId == 8);
        const list = certificateGroupingDetailsData.allocationCostList;
        const rows = buildCostAllocationRows(productListData, itemCodeOptionsMemo);
        rows.forEach((row) => {
          let certificateNumber = row.label;
          let prefix = 0;
          const lastSlashIndex = row.label.lastIndexOf("/");
          if (lastSlashIndex !== -1) {
            certificateNumber = row.label.substring(0, lastSlashIndex);
            prefix = row.label.substring(lastSlashIndex + 1);
          }

          const existingAllocation = list.find((x) => {
            const [xCertificateNumber, xPrefix = 0] = x.certificateNumber.split("/");

            return (
              xCertificateNumber === certificateNumber &&
              Number(xPrefix) === Number(prefix || 0)
            );
          });

          row.value = existingAllocation ? itemCodeOptionsMemo?.find((i) => i.value === existingAllocation.itemCode) ?? null : null;
          row.sgbpCertificateGroupingId = existingAllocation ? existingAllocation.sgbpCertificateGroupingId : 0;
          row.finalCertificateAndCostAllocationId = existingAllocation ? existingAllocation.finalCertificateAndCostAllocationId : 0;
        });
        setCostAllocationList(rows);
        const peerReviewerOption = approverOptionsMemo?.find((item) => item.value === certificateGroupingDetailsData?.peerReviewerId);
        const approverOption = approverOptionsMemo?.find((item) => item.value === certificateGroupingDetailsData?.approverId);
        setPeerReviewer(peerReviewerOption || null);
        setApprover(approverOption || null);
        setInternalNotes(certificateGroupingDetailsData?.commentFromCaseOfficer || "");
      }
    }
  }, [certificateGroupingDetailsLoading, certificateGroupingDetailsSuccess, certificateGroupingDetailsData, productListData, itemCodeOptionsMemo, approverOptionsMemo]);

  useEffect(() => {
    if (!saveCertificateGroupingLoading && saveCertificateGroupingData && saveCertificateGroupingSuccess) {
      if (saveCertificateGroupingData?.hasError) {
        ToastService.error(saveCertificateGroupingData?.errorMessage);
      }
      else if (!isReview) {
        success("Certificate Grouping Request Updated Successfully!");
      }
      setIsReview(false);
    }
  }, [saveCertificateGroupingLoading, saveCertificateGroupingData, saveCertificateGroupingSuccess]);

  useEffect(() => {
    if (!sendCertificateGroupingForReviewLoading && sendCertificateGroupingForReviewSuccess && sendCertificateGroupingForReviewData) {
      if (!sendCertificateGroupingForReviewData?.hasError) {
        ToastService.success("Review Request sent successfully");
        handleFinalCostAllocation();
        handleSave();
        navigate("/viewApplicationTaskList");
      } else {
        ToastService.error(sendCertificateGroupingForReviewData.errorMessage);
      }
    }
  }, [sendCertificateGroupingForReviewLoading, sendCertificateGroupingForReviewSuccess, sendCertificateGroupingForReviewData]);

  // const buildCostAllocationRows = (list, itemCodes, existingRows = []) => {
  //   const uniqueMap = new Map();
  //   list.forEach((item) => {
  //     const certNumber = item.applicationNumber;
  //     const certPrefix = item.certPrefix;
  //     const key = certPrefix ? `${certNumber}/${certPrefix}` : certNumber;

  //     if (!uniqueMap.has(key)) {
  //       uniqueMap.set(key, true);
  //     }
  //   });

  //   return Array.from(uniqueMap.keys()).map((label, index) => {
  //     const existing = existingRows?.find((row) => row.label === label);
  //     return {
  //       id: index + 1,
  //       label,
  //       options: itemCodes,
  //       value: existing ? existing.value : null,
  //     };
  //   });
  // };


  const buildCostAllocationRows = (list, itemCodes, existingRows = []) => {
    const uniqueMap = new Map();
    list.forEach((item) => {
      const certNumber = item.applicationNumber;
      const certPrefix = item.certPrefix;
      const key = certPrefix ? `${certNumber}/${certPrefix}` : certNumber;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, true);
      }
    });

    return Array.from(uniqueMap.keys()).map((label, index) => {
      const existingExact = existingRows?.find((row) => row.label === label);
      const existingByIndex = existingRows?.[index];

      return {
        id: index + 1,
        label,
        options: itemCodes,
        value: existingExact ? existingExact.value : null, // <-- cleared when label changes
        sgbpCertificateGroupingId: existingExact?.sgbpCertificateGroupingId ?? existingByIndex?.sgbpCertificateGroupingId ?? 0,
        finalCertificateAndCostAllocationId: existingExact?.finalCertificateAndCostAllocationId ?? existingByIndex?.finalCertificateAndCostAllocationId ?? 0,
      };
    });
  };

  const handleCostAllocationChange = useCallback((rowId, selectedOption) => {
    setCostAllocationList((prevRows) => prevRows.map((row) => row.id === rowId ? { ...row, value: selectedOption ?? null } : row));
    setFormErrors((prev) => {
      if (!prev.costAllocation[rowId]) return prev;
      const nextCostAllocation = { ...prev.costAllocation };
      delete nextCostAllocation[rowId];
      return { ...prev, costAllocation: nextCostAllocation };
    });
  }, []);

  const parseApplicationNumber = (value) => {
    const str = String(value ?? "");
    const match = str.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) return null;

    const [, prefix, digitsPart] = match;
    return {
      prefix,
      number: parseInt(digitsPart, 10),
      width: digitsPart.length,
    };
  };
  const validateApplicationNumber = (referenceValue, newValue, apiFlage, isExisted) => {
    const referenceParsed = parseApplicationNumber(referenceValue);
    const newParsed = parseApplicationNumber(newValue);

    if (!referenceParsed || !newParsed) {
      ToastService.error("Invalid application number format.");
      return false;
    }

    // Letters prefix must never change
    if (referenceParsed.prefix !== newParsed.prefix) {
      ToastService.error("Prefix cannot be changed.");
      return false;
    }

    const diff = newParsed.number - referenceParsed.number;
    // Width must stay the same, UNLESS the number just crossed into needing
    // one more digit (e.g. 99999 -> 100000), in which case width can grow by exactly 1.
    const expectedWidthForNewNumber = String(newParsed.number).length;
    const widthGrewNaturally =
      newParsed.width === Math.max(referenceParsed.width, expectedWidthForNewNumber) &&
      newParsed.width >= referenceParsed.width;

    if (!widthGrewNaturally) {
      ToastService.error("Invalid number format — digit padding is incorrect.");
      return false;
    }

    let isRecordExisted = productListData?.filter((item) => item.applicationNumber == newValue)

    // if (diff === 0 || diff === 1) return true; // same as reference, or +1
    if (diff === 0) return true; // same as reference, or +1

    if (diff === 1) {
      if (isExisted) {
        if (isRecordExisted.length > 0) {
          return true
        } else {
          return false
        }
      }
      return true
    }



    if (diff < 0) {
      // if (!apiFlage) {
      //   return true
      // }
      ToastService.error("This number cannot be decreased.");
      return false;
    }
    if (diff > 0) {
      if (!apiFlage) {
        return true
      }
      // ToastService.error("This number cannot be decreased.");
      // return false;
    }

    ToastService.error("Please increase by 1 at a time — the number cannot skip.");
    return false;
  };

  // const handleApplicationNumberBlur = useCallback((id, value) => {
  //   const index = productList.findIndex((item) => item.sgbpProductId === id);
  //   if (index === -1) return false;

  //   const current = productList[index];
  //   const ownParsed = parseApplicationNumber(current.applicationNumber);
  //   if (!ownParsed) {
  //     ToastService.error("Invalid application number format.");
  //     return false;
  //   }

  //   const referenceValue = index === 0
  //     ? (firstRowOriginalRef.current ?? current.applicationNumber)
  //     : productList[index - 1].applicationNumber;

  //   const isValid = validateApplicationNumber(referenceValue, value);
  //   if (!isValid) return false;

  //   setProductList((prev) =>
  //     prev.map((item) => (item.sgbpProductId === id ? { ...item, applicationNumber: value } : item))
  //   );
  //   return true;
  // }, [productList]);

  // ganarate formate 
  const handleAppNumberChange = useCallback((id, value) => {
    setAppNumberValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleApplicationNumberBlur = useCallback(async (id, value) => {
    try {
      const response = await validateCertificateNumberForAssignment({ CertificateNumber: value, }).unwrap();
      let { isCertificateNumberAssigned, nextCertificateSequenceNumber, isExisted } = response;

      // if (!isCertificateNumberAssigned) {
      //   const typedValue = appNumberValues[id];
      //   if (typedValue === value) return; // nothing changed, skip
      //   // const referenceParsed = parseApplicationNumber(typedValue);
      //   // const newParsed = parseApplicationNumber(value);
      //   // const diff = newParsed.number - referenceParsed.number;
      //     const isValid = validateApplicationNumber(typedValue, value, isCertificateNumberAssigned);
      // }

      const newParsed = parseApplicationNumber(nextCertificateSequenceNumber);
      const index = productList.findIndex((item) => item.sgbpProductId === id);
      const oldValue = productListData?.filter((item) => item.sgbpProductId === id);
      if (value == oldValue[0]?.applicationNumber) {
        // return true;
        setProductList((prev) =>
          prev.map((item) => (item.sgbpProductId === id ? { ...item, applicationNumber: value } : item))
        );
        return true;
      }
      if (index === -1) return false;

      const current = productList[index];
      const ownParsed = parseApplicationNumber(current.applicationNumber);
      if (!ownParsed) {
        ToastService.error("Invalid application number format.");
        return false;
      }
      const referenceValue = index === 0 ? (firstRowOriginalRef.current ?? current.applicationNumber) : productList[index - 1].applicationNumber;
      const isValid = validateApplicationNumber(referenceValue, value, isCertificateNumberAssigned, isExisted);
      if (!isValid) return false;
      // ---- API call on blur ----
      if (response?.hasError) {
        ToastService.error(response.errorMessage || "Certificate number already assigned or invalid.");
        return false;
      }
    } catch (err) {
      console.log(err);
      ToastService.error("Something went wrong while validating certificate number.");
      return false;
    }
    // ---------------------------

    setProductList((prev) =>
      prev.map((item) => (item.sgbpProductId === id ? { ...item, applicationNumber: value } : item))
    );
    return true;
  }, [productList, validateCertificateNumberForAssignment]);

  const handleProductChange = useCallback((id, field, value) => {
    setProductList((prev) =>
      prev.map((item) => (item.sgbpProductId === id ? { ...item, [field]: value } : item))
    );
  }, []);

  const validateForm = useCallback(() => {
    const errors = { costAllocation: {}, peerReviewer: "", approver: "" };
    let firstErrorMessage = "";

    costAllocationList.forEach((row) => {
      if (!row.value) {
        errors.costAllocation[row.id] = "Item Code is required.";
        if (!firstErrorMessage) firstErrorMessage = `Please select Item Code for "${row.label}".`;
      }
    });

    if (!peerReviewer) {
      errors.peerReviewer = "Peer Reviewer is required.";
      if (!firstErrorMessage) firstErrorMessage = "Please select Peer Reviewer.";
    }

    if (!approver) {
      errors.approver = "Approver is required.";
      if (!firstErrorMessage) firstErrorMessage = "Please select Approver.";
    }

    setFormErrors(errors);

    if (firstErrorMessage) {
      error(firstErrorMessage);
      return false;
    }
    return true;
  }, [costAllocationList, peerReviewer, approver]);

  const handleSave = useCallback(() => {
    if (!validateForm()) return;
    const applicationId = decryptAESID(id?.toString(), false);


    const req = {
      comments: internalNotes,
      peerReviewerId: peerReviewer?.value,
      approverId: approver?.value,
      sGBPSApplicationId: applicationId,

      costAllocationRequestListRequest: costAllocationList.map((row) => {
        const [certificateNumber, certPrefix] = row.label.split("/");
        const prefix = Number(certPrefix);

        return {
          itemCode: row.value?.value,
          certificateNumber: certificateNumber + (certPrefix ? `/${certPrefix}` : ""),
          certPrefix: certPrefix === undefined || prefix === 0 ? null : prefix,
          sGBPSApplicationId: applicationId,
          // SgbpCertificateGroupingId: row?.sgbpCertificateGroupingId ?? 0,
        };
      }),

      modelGroupingRequestListRequest: productList.map(
        ({ textValue, rating, certPrefix, sgbpProductId, applicationNumber }) => ({
          sgbpProductId,
          textValue,
          rating: Number(rating),
          certPrefix: Number(certPrefix) === 0 ? null : Number(certPrefix),
          certificateNumber: String(applicationNumber),
        })
      ),
    };
    saveCertificateGrouping(req);
  }, [validateForm, id, internalNotes, peerReviewer, approver, costAllocationList, productList, saveCertificateGrouping]);

  const handleReviewRequest = useCallback(() => {
    if (!validateForm()) return;

    setIsReview(true);
    const req = { sGBPProductInformationId: decryptAESID(id) };
    sendCertificateGroupingForReview(req);
  }, [validateForm, id, sendCertificateGroupingForReview]);

  const handleBack = () => {
    navigate(`/caseOfficerEvaluation/${id}`);
  };

  const handleFinalCostAllocation = useCallback(() => {
    if (!validateForm()) return;
    const applicationId = decryptAESID(id?.toString(), false);


    const req = {
      sGBPSApplicationId: applicationId,
      finalCostAllocationRequestListRequest: costAllocationList.map((row) => {
        const [certificateNumber, certPrefix] = row.label.split("/");

        return {
          itemCode: row.value?.value,
          certificateNumber: certificateNumber + (certPrefix ? `/${certPrefix}` : ""),
          finalCertificateAndCostAllocationId: row?.finalCertificateAndCostAllocationId ?? 0,
        };
      })
    };

    sendFinalCertificateAndCostAllocation(req);
  }, [validateForm, id, internalNotes, peerReviewer, approver, costAllocationList, productList, saveCertificateGrouping]);




  const handlePeerReviewerChange = useCallback((selected) => {
    setPeerReviewer(selected);
    setFormErrors((prev) => (prev.peerReviewer ? { ...prev, peerReviewer: "" } : prev));
  }, []);

  const handleApproverChange = useCallback((selected) => {
    setApprover(selected);
    setFormErrors((prev) => (prev.approver ? { ...prev, approver: "" } : prev));
  }, []);

  const isPageLoading = productsAndServicesDropDownsLoading || productListLoading || certificateGroupingDetailsLoading;

  return (
    <>
      {isPageLoading ? (
        <PageLoader />
      ) : (
        <div className="certificate-grouping-list-page">
          <div className="crud-requirement-list">
            <div className="corporate-member-list">
              <ModalGroupingTable
                data={productList}
                onChange={handleProductChange}
                onApplicationNumberBlur={handleApplicationNumberBlur}
                isDisabled={isApprove}
              />

              <CostAllocation
                rows={costAllocationList}
                onChange={handleCostAllocationChange}
                errors={formErrors.costAllocation}
                isDisabled={isApprove}
              />

              <div className="table-title mt-5 mb-4">
                <h3>Internal Notes</h3>
              </div>
              <div className="mt-2">
                <label className="form-label">Comments from Case Officer</label>
                <textarea
                  className="form-control mt-1"
                  rows="4"
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  disabled={isApprove}
                ></textarea>
              </div>
              <div className="mt-2">
                <label className="form-label">Peer Reviewer Comment</label>
                <textarea
                  className="form-control mt-1"
                  rows="4"
                  value={getPeerReviewEvaluationDetailsByIdData?.peerReviewerComment}
                  maxLength={200}
                  disabled
                ></textarea>
              </div>
              <div className="mt-2">
                <label className="form-label">Approver Reviewer Comment</label>
                <textarea
                  className="form-control mt-1"
                  rows="4"
                  disabled
                  value={getPeerReviewEvaluationDetailsByIdData?.finalApproverComment}
                ></textarea>
              </div>

              <div className="table-title mt-5 mb-4">
                <h3>Assign to</h3>
              </div>
              <div className="dropdown-select-wrapper">
                <div className="d-flex align-items-center mb-4">
                  <label className="form-label mb-0 w-20">
                    Peer Reviewer <span className="text-danger">*</span>
                  </label>
                  <div style={{ flex: 1 }}>
                    <Select
                      className="custom-checkbox-select"
                      value={peerReviewer}
                      options={approverOptions}
                      onChange={handlePeerReviewerChange}
                      isDisabled={isApprove}
                    />
                    {formErrors.peerReviewer && (
                      <div className="field-error text-danger" style={{ fontSize: "12px", marginTop: "2px" }}>
                        {formErrors.peerReviewer}
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <label className="form-label mb-0 w-20">
                    Approver <span className="text-danger">*</span>
                  </label>
                  <div style={{ flex: 1 }}>
                    <Select
                      className="custom-checkbox-select"
                      value={approver}
                      onChange={handleApproverChange}
                      options={approverOptions}
                      isDisabled={isApprove}
                    />
                    {formErrors.approver && (
                      <div className="field-error text-danger" style={{ fontSize: "12px", marginTop: "2px" }}>
                        {formErrors.approver}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between mt-5">
                <Buttons
                  buttonText="Back"
                  buttonTypeClassName="black-btn"
                  onClick={handleBack}
                />

                <div className="d-flex gap-3">
                  <Buttons
                    buttonText="Save"
                    buttonTypeClassName="search-btn"
                    onClick={handleSave}
                    isLoading={!isReview && saveCertificateGroupingLoading}
                    isDisable={saveCertificateGroupingLoading || isApprove}
                  />
                  {isApprove ? <Buttons
                    buttonText={"Approve"}
                    buttonTypeClassName="success-btn"
                  /> :
                    <Buttons
                      buttonText={"Request for Review"}
                      buttonTypeClassName="success-btn"
                      onClick={handleReviewRequest}
                      isLoading={sendCertificateGroupingForReviewLoading}
                      isDisable={sendCertificateGroupingForReviewLoading}
                    />}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CertificateGroupingRequestList;